/**
 * ADMIN-12 — client d'authentification de la console.
 *
 * Transport (INV-13) : l'access token porteur du claim `adminConsole` vit EN
 * MÉMOIRE (jamais localStorage — une XSS ne peut pas l'exfiltrer), et le refresh
 * est un cookie httpOnly posé par l'API (`credentials: "include"` sur chaque
 * appel). Au (re)chargement de la page, `bootstrap()` tente un refresh silencieux :
 * le cookie survit au rechargement, pas la mémoire.
 *
 * Flux : login (mot de passe) → step-up (TOTP/secours) → session ; puis
 * fetchWithAuth rejoue un refresh unique sur 401 avant d'abandonner vers le login.
 *
 * En dev, Vite proxifie `/v1` vers l'API (même origine) : `API_BASE` est vide. En
 * prod, la SPA (admin.livemessage.fr) appelle l'API (api.livemessage.fr) via
 * `VITE_API_BASE` — le cookie voyage grâce au CORS credentials d'ADMIN-5.
 */

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

export interface ConsoleUser {
  id: string;
  email: string;
  displayName: string;
  handle: string | null;
  isAdmin: boolean;
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number
  ) {
    super(message);
    this.name = "AuthError";
  }
}

let accessToken: string | null = null;
let currentUser: ConsoleUser | null = null;

export const getUser = (): ConsoleUser | null => currentUser;

async function readError(res: Response): Promise<AuthError> {
  let body: { error?: string; code?: string } = {};
  try {
    body = (await res.json()) as { error?: string; code?: string };
  } catch {
    /* corps non-JSON */
  }
  return new AuthError(body.error || `Erreur ${res.status}`, body.code, res.status);
}

/** Facteur 1 : mot de passe ⇒ pré-token de step-up (n'ouvre aucune session). */
export async function login(email: string, password: string): Promise<{ stepUpToken: string }> {
  const res = await fetch(`${API_BASE}/v1/console/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw await readError(res);
  return (await res.json()) as { stepUpToken: string };
}

/** Facteur 2 : pré-token + code ⇒ session console (access token + cookie refresh). */
export async function stepUp(stepUpToken: string, code: string): Promise<ConsoleUser> {
  const res = await fetch(`${API_BASE}/v1/console/step-up`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ stepUpToken, code }),
  });
  if (!res.ok) throw await readError(res);
  const data = (await res.json()) as { accessToken: string; user: ConsoleUser };
  accessToken = data.accessToken;
  currentUser = data.user;
  return data.user;
}

/** Rejoue le cookie de session console ⇒ nouvel access token porteur du claim. */
export async function refresh(): Promise<ConsoleUser> {
  const res = await fetch(`${API_BASE}/v1/console/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    accessToken = null;
    currentUser = null;
    throw await readError(res);
  }
  const data = (await res.json()) as { accessToken: string; user: ConsoleUser };
  accessToken = data.accessToken;
  currentUser = data.user;
  return data.user;
}

// ── Enrôlement 2FA (1er accès d'un admin fraîchement promu) ──────────────────
// Un admin SANS TOTP est refusé au /v1/console/login (403 CONSOLE_TOTP_REQUIRED),
// et les routes d'enrôlement exigent `requireAdmin` (un JWT facteur-1), que le
// login console ne délivre pas. On récupère donc un access token STANDARD via
// /v1/auth/login (facteur 1 régulier, sans captcha) — gardé EN MÉMOIRE le temps
// de l'enrôlement, jamais persisté — pour appeler /v1/console/totp/enroll(+confirm).
let enrollToken: string | null = null;

/** Extrait le secret base32 de l'URI otpauth:// (repli si le QR n'est pas scannable). */
function secretFromOtpauth(url: string): string {
  try {
    return new URL(url).searchParams.get("secret") ?? "";
  } catch {
    return "";
  }
}

/**
 * Démarre l'enrôlement : facteur 1 régulier ⇒ access token en mémoire ⇒ génère un
 * secret TOTP côté serveur. Rend l'URI otpauth:// (pour le QR) et le secret (saisie
 * manuelle). `credentials: "omit"` : on ne veut PAS poser le cookie de session de
 * l'app sur l'origine console — seul l'access token (corps) nous sert.
 */
export async function enrollBegin(
  email: string,
  password: string
): Promise<{ otpauthUrl: string; secret: string }> {
  const loginRes = await fetch(`${API_BASE}/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify({ email, password, deviceId: "admin-console" }),
  });
  if (!loginRes.ok) throw await readError(loginRes);
  enrollToken = ((await loginRes.json()) as { accessToken: string }).accessToken;

  const res = await fetch(`${API_BASE}/v1/console/totp/enroll`, {
    method: "POST",
    headers: { Authorization: `Bearer ${enrollToken}` },
  });
  if (!res.ok) throw await readError(res);
  const { otpauthUrl } = (await res.json()) as { otpauthUrl: string };
  return { otpauthUrl, secret: secretFromOtpauth(otpauthUrl) };
}

/** Confirme l'enrôlement avec un code TOTP ⇒ codes de secours (servis UNE fois). */
export async function enrollConfirm(code: string): Promise<string[]> {
  if (!enrollToken) throw new AuthError("Enrôlement non démarré", "NO_ENROLLMENT");
  const res = await fetch(`${API_BASE}/v1/console/totp/enroll/confirm`, {
    method: "POST",
    headers: { Authorization: `Bearer ${enrollToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ code: code.trim() }),
  });
  if (!res.ok) throw await readError(res);
  const { recoveryCodes } = (await res.json()) as { recoveryCodes: string[] };
  enrollToken = null; // usage terminé : le token facteur-1 ne sert plus à rien
  return recoveryCodes;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/v1/console/logout`, { method: "POST", credentials: "include" });
  } finally {
    accessToken = null;
    currentUser = null;
  }
}

/** Au chargement : tente de restaurer une session via le cookie. Null si aucune. */
export async function bootstrap(): Promise<ConsoleUser | null> {
  try {
    return await refresh();
  } catch {
    return null;
  }
}

/**
 * Appel authentifié aux routes protégées. Ajoute le Bearer + les credentials, et
 * rejoue UN refresh sur 401 (access token périmé à 15 min) avant d'abandonner.
 */
export async function fetchWithAuth(path: string, init: RequestInit = {}): Promise<Response> {
  const doFetch = () =>
    fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        ...(init.headers as Record<string, string> | undefined),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
    });

  let res = await doFetch();
  if (res.status === 401) {
    // Access token périmé : un seul refresh, puis on rejoue une fois.
    try {
      await refresh();
    } catch {
      throw new AuthError("Session expirée", "SESSION_EXPIRED", 401);
    }
    res = await doFetch();
  }
  return res;
}

/** Helper JSON typé pour les écrans (lève AuthError sur statut non-2xx). */
export async function getJson<T>(path: string): Promise<T> {
  const res = await fetchWithAuth(path);
  if (!res.ok) throw await readError(res);
  return (await res.json()) as T;
}

export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: "POST",
    ...(body === undefined ? {} : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });
  if (!res.ok) throw await readError(res);
  return (await res.json()) as T;
}
